import React, { useState } from 'react'
import { ScrollReveal } from './ScrollReveal'

const providers = [
  { id: 'all', name: 'All Models' },
  { id: 'openai', name: 'OpenAI' },
  { id: 'anthropic', name: 'Anthropic' },
  { id: 'google', name: 'Google' },
  { id: 'meta', name: 'Meta' },
  { id: 'mistral', name: 'Mistral' },
  { id: 'neoserve', name: 'Neoserve' },
]

const models = [
  // OpenAI
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'openai', providerLabel: 'OpenAI', tags: ['Vision', 'Reasoning'], context: '128K', badge: 'Latest' },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'openai', providerLabel: 'OpenAI', tags: ['Fast', 'Affordable'], context: '128K' },
  { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', provider: 'openai', providerLabel: 'OpenAI', tags: ['Reasoning'], context: '128K' },
  { id: 'o1-preview', name: 'o1 Preview', provider: 'openai', providerLabel: 'OpenAI', tags: ['Reasoning', 'STEM'], context: '128K', badge: 'New' },
  // Anthropic
  { id: 'claude-3-5-sonnet', name: 'Claude 3.5 Sonnet', provider: 'anthropic', providerLabel: 'Anthropic', tags: ['Coding', 'Analysis'], context: '200K', badge: 'Popular' },
  { id: 'claude-3-5-haiku', name: 'Claude 3.5 Haiku', provider: 'anthropic', providerLabel: 'Anthropic', tags: ['Fast', 'Affordable'], context: '200K' },
  { id: 'claude-3-opus', name: 'Claude 3 Opus', provider: 'anthropic', providerLabel: 'Anthropic', tags: ['Powerful'], context: '200K' },
  // Google
  { id: 'gemini-1-5-pro', name: 'Gemini 1.5 Pro', provider: 'google', providerLabel: 'Google', tags: ['Vision', 'Long Context'], context: '1M', badge: 'New' },
  { id: 'gemini-1-5-flash', name: 'Gemini 1.5 Flash', provider: 'google', providerLabel: 'Google', tags: ['Fast', 'Vision'], context: '1M' },
  { id: 'gemini-2-0-flash', name: 'Gemini 2.0 Flash', provider: 'google', providerLabel: 'Google', tags: ['Fast', 'Multimodal'], context: '1M', badge: 'Latest' },
  // Meta
  { id: 'llama-3-1-405b', name: 'Llama 3.1 405B', provider: 'meta', providerLabel: 'Meta', tags: ['Open Source', 'Powerful'], context: '128K' },
  { id: 'llama-3-1-70b', name: 'Llama 3.1 70B', provider: 'meta', providerLabel: 'Meta', tags: ['Open Source', 'Fast'], context: '128K' },
  { id: 'llama-3-2-vision', name: 'Llama 3.2 Vision', provider: 'meta', providerLabel: 'Meta', tags: ['Open Source', 'Vision'], context: '128K', badge: 'New' },
  // Mistral
  { id: 'mistral-large', name: 'Mistral Large', provider: 'mistral', providerLabel: 'Mistral', tags: ['Multilingual', 'Reasoning'], context: '128K' },
  { id: 'mistral-nemo', name: 'Mistral Nemo', provider: 'mistral', providerLabel: 'Mistral', tags: ['Efficient', 'Multilingual'], context: '128K' },
  { id: 'codestral', name: 'Codestral', provider: 'mistral', providerLabel: 'Mistral', tags: ['Coding'], context: '32K' },
  // Neoserve
  { id: 'ns-speed', name: 'NS Speed', provider: 'neoserve', providerLabel: 'Neoserve', tags: ['APAC', 'Fast'], context: '16K', badge: 'APAC' },
  { id: 'ns-balanced', name: 'NS Balanced', provider: 'neoserve', providerLabel: 'Neoserve', tags: ['APAC', 'Balanced'], context: '32K', badge: 'APAC' },
  { id: 'ns-pro', name: 'NS Pro', provider: 'neoserve', providerLabel: 'Neoserve', tags: ['APAC', 'Powerful'], context: '128K', badge: 'APAC' },
]

const providerColors: Record<string, { dot: string; label: string }> = {
  openai:     { dot: 'bg-emerald-500', label: 'text-emerald-700 bg-emerald-50' },
  anthropic:  { dot: 'bg-orange-500',  label: 'text-orange-700 bg-orange-50' },
  google:     { dot: 'bg-blue-500',    label: 'text-blue-700 bg-blue-50' },
  meta:       { dot: 'bg-sky-500',     label: 'text-sky-700 bg-sky-50' },
  mistral:    { dot: 'bg-violet-500',  label: 'text-violet-700 bg-violet-50' },
  neoserve:  { dot: 'bg-indigo-500',  label: 'text-indigo-700 bg-indigo-50' },
}

export function ModelCatalog() {
  const [activeProvider, setActiveProvider] = useState('all')

  const filtered = activeProvider === 'all'
    ? models
    : models.filter(m => m.provider === activeProvider)

  return (
    <section className="py-24 bg-[#050810] relative overflow-hidden">
      <div className="container">
        {/* Header */}
        <ScrollReveal className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-900/50 border border-indigo-700/50 rounded-full text-xs font-medium text-indigo-300 mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
            20+ Models Available
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white whitespace-nowrap">
            Connect to every leading AI model
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            One API key. Switch between providers instantly — no re-integration needed.
          </p>
        </ScrollReveal>

        {/* Provider Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {providers.map(p => (
            <button
              key={p.id}
              onClick={() => setActiveProvider(p.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeProvider === p.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white/[0.04] text-gray-400 hover:bg-white/[0.08] hover:text-white border border-white/[0.06]'
              }`}
            >
              {p.id !== 'all' && (
                <span className={`inline-block h-2 w-2 rounded-full mr-2 ${providerColors[p.id]?.dot}`} />
              )}
              {p.name}
            </button>
          ))}
        </div>

        {/* Model Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(model => {
            const colors = providerColors[model.provider]
            return (
              <div
                key={model.id}
                className="group bg-white/[0.03] border border-white/[0.07] hover:border-white/[0.15] rounded-xl p-4 transition-all hover:bg-white/[0.05]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`h-2 w-2 rounded-full shrink-0 ${colors.dot}`} />
                      <span className="text-sm font-semibold text-white truncate">{model.name}</span>
                      {model.badge && (
                        <span className="shrink-0 text-xs px-1.5 py-0.5 rounded font-medium bg-indigo-900 text-indigo-300 border border-indigo-700">
                          {model.badge}
                        </span>
                      )}
                    </div>
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${colors.label} mb-3`}>
                      {model.providerLabel}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {model.tags.map(tag => (
                        <span key={tag} className="text-xs px-2 py-0.5 bg-gray-800 text-gray-400 rounded border border-gray-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs text-gray-500">Context</p>
                    <p className="text-sm font-mono font-semibold text-gray-300">{model.context}</p>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between">
                  <code className="text-xs text-gray-500 font-mono">{model.id}</code>
                  <span className="text-xs text-gray-600 group-hover:text-indigo-400 transition-colors">OpenAI-compatible →</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer note */}
        <p className="text-center text-sm text-gray-500 mt-10">
          All models are accessible via a single OpenAI-compatible endpoint.{' '}
          <span className="text-gray-400">More models added regularly.</span>
        </p>
      </div>
    </section>
  )
}
